import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import Navigation from "@/components/site/Navigation";
import Footer from "@/components/site/Footer";
import { useReveal } from "@/hooks/useReveal";
import { useYogoWidget } from "@/hooks/useYogoWidget";
import { trackEvent } from "@/lib/analytics";

const YOGO_PROFILE = "https://luftpilates.yogobooking.pt/frontend/index.html#/my-profile";

export default function Reservar() {
  const navigate = useNavigate();
  const ref = useReveal();
  const yogoFiredRef = useRef(false);

  // Ensures the YOGO loader is present and re-kicks the mount if it remains empty
  // after SPA navigation. Does NOT simulate or replace the widget.
  useYogoWidget();

  // Set a friendlier document title while this page is mounted
  useEffect(() => {
    const prev = document.title;
    document.title = "Reservar · Lu.ft Pilates Studio";
    return () => {
      document.title = prev;
    };
  }, []);

  // First interaction with the YOGO calendar container fires reservar_click once per page load.
  // We use pointerdown so it captures both mouse and touch without interfering with YOGO's own handlers.
  const handleYogoInteraction = () => {
    if (yogoFiredRef.current) return;
    yogoFiredRef.current = true;
    trackEvent("reservar_click", { cta_location: "reservar_page" });
  };

  return (
    <main className="bg-[var(--luft-bg)]" ref={ref} data-testid="reservar-page">
      <Navigation />

      {/* Hero */}
      <section
        className="relative w-full overflow-hidden bg-[var(--luft-bg)] pt-32 sm:pt-36 lg:pt-40 pb-20 sm:pb-24"
        data-testid="reservar-hero"
      >
        {/* Coastal ambient */}
        <div
          className="pointer-events-none absolute -top-40 -right-32 w-[65vw] h-[65vw] rounded-full opacity-[0.30]"
          style={{
            background:
              "radial-gradient(circle, rgba(181,199,211,0.72) 0%, transparent 65%)",
          }}
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute -bottom-32 -left-24 w-[50vw] h-[50vw] rounded-full opacity-[0.18]"
          style={{
            background:
              "radial-gradient(circle, rgba(82,96,254,0.28) 0%, transparent 72%)",
          }}
          aria-hidden="true"
        />

        <div className="relative max-w-[1440px] mx-auto px-6 sm:px-10 lg:px-16">
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 mb-12 text-[var(--luft-ink-soft)] text-[0.7rem] uppercase tracking-[0.28em] hover:text-[var(--luft-blue)] transition-colors"
            data-testid="reservar-back-btn"
          >
            <ArrowLeft size={14} strokeWidth={1.4} />
            voltar ao Lu.ft
          </button>

          <div className="grid grid-cols-12 gap-y-10 lg:gap-x-14 items-end">
            <div className="col-span-12 lg:col-span-8 reveal">
              <p className="eyebrow mb-8">Reservar · calendário</p>
              <h1
                className="editorial-heading text-[var(--luft-ink)] text-[clamp(2.6rem,7vw,6rem)] leading-[0.96] max-w-4xl"
                data-testid="reservar-title"
              >
                Encontra o teu <em className="editorial-italic">ritmo</em>.
              </h1>
              <p
                className="mt-8 max-w-xl font-body font-light text-[var(--luft-ink-soft)] text-[1.02rem] leading-relaxed"
                data-testid="reservar-subtitle"
              >
                Escolhe a prática e o horário que melhor se encaixam em ti.
              </p>
            </div>

            <div className="col-span-12 lg:col-span-4 lg:pl-6 reveal">
              <div className="border-t border-[var(--luft-line)] pt-8">
                <p
                  className="text-[var(--luft-ink-soft)] mb-3"
                  style={{
                    fontFamily: "Montserrat, sans-serif",
                    fontSize: "0.6rem",
                    letterSpacing: "0.28em",
                    textTransform: "uppercase",
                  }}
                >
                  Já é aluno(a)?
                </p>
                <p className="font-display italic text-[var(--luft-ink)] text-xl mb-6 leading-snug">
                  Aceda à sua área pessoal para gerir aulas e marcações.
                </p>
                <a
                  href={YOGO_PROFILE}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2.5 text-[var(--luft-ink)] text-[0.72rem] uppercase tracking-[0.28em] border-b border-[var(--luft-ink)]/40 pb-2 hover:text-[var(--luft-blue)] hover:border-[var(--luft-blue)] transition-colors"
                  data-testid="reservar-my-profile-link"
                >
                  Entrar na área pessoal
                  <ExternalLink size={13} strokeWidth={1.4} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* YOGO calendar canvas */}
      <section
        className="relative bg-[var(--luft-bg-alt)] py-16 sm:py-20 lg:py-24"
        data-testid="reservar-calendar-section"
      >
        <div className="max-w-[1440px] mx-auto px-6 sm:px-10 lg:px-16">
          <div
            className="relative bg-[var(--luft-bg)] shadow-[0_28px_80px_-40px_rgba(30,33,38,0.22)] overflow-hidden"
            style={{ borderRadius: "var(--radius-hero)" }}
            onPointerDown={handleYogoInteraction}
          >
            <div className="px-6 sm:px-10 lg:px-14 py-10 sm:py-14 lg:py-16">
              {/* Official YOGO widget mount point. The global script (to be added to <head>) will hydrate this element. */}
              <div
                className="yogo-calendar"
                data-testid="yogo-calendar-mount"
                aria-label="Calendário de reservas Lu.ft"
              />
            </div>
          </div>

          <p
            className="mt-8 text-center text-[var(--luft-muted)] max-w-2xl mx-auto leading-relaxed"
            style={{
              fontFamily: "Montserrat, sans-serif",
              fontSize: "0.68rem",
              letterSpacing: "0.22em",
              textTransform: "uppercase",
            }}
            data-testid="reservar-yogo-note"
          >
            Reservas geridas pela YOGO · sistema oficial do estúdio
          </p>
        </div>
      </section>

      {/* Small editorial footer above main Footer */}
      <section className="relative bg-[var(--luft-bg)] py-24 sm:py-32">
        <div className="max-w-[900px] mx-auto px-6 sm:px-10 lg:px-16 text-center">
          <p className="font-display italic text-[var(--luft-ink)] text-2xl sm:text-3xl leading-snug">
            "Um estúdio pequeno, atento e cuidado,<br />
            onde cada gesto importa."
          </p>
          <p className="eyebrow mt-8">Lu.ft · Entroncamento</p>
        </div>
      </section>

      <Footer />
    </main>
  );
}
